import { Box, Row } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { IconEye } from '@tabler/icons-react';
import { FavoriteHeartIcon } from 'components/marketplace/shared/FavoriteHeartIcon';
import { AnimatePresence, motion, type Transition, useReducedMotion } from 'framer-motion';

import type { PropertyEngagement } from '../types';

const countFormatter = new Intl.NumberFormat('en-US');
const compactCountFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });
const compactUnits = ['', 'K', 'M', 'B', 'T'] as const;
const MIN_VISIBLE_VIEW_COUNT = 11;
const MIN_VISIBLE_SAVE_COUNT = 3;
const saveCountTransition: Transition = { duration: 0.24, ease: [0.22, 1, 0.36, 1] };

const formatActivityCount = (count: number) => {
  if (count < 1000) return countFormatter.format(count);

  let unitIndex = 0;
  let value = count;
  while (value >= 1000 && unitIndex < compactUnits.length - 1) {
    value /= 1000;
    unitIndex += 1;
  }

  let rounded = Math.round(value * 10) / 10;
  if (rounded >= 1000 && unitIndex < compactUnits.length - 1) {
    rounded = 1;
    unitIndex += 1;
  }
  return `${compactCountFormatter.format(rounded)}${compactUnits[unitIndex]}`;
};

const AnimatedSaveCount = ({ count }: { count: number }) => {
  const reduceMotion = useReducedMotion();

  return (
    <span className="relative inline-flex overflow-hidden align-baseline tabular-nums">
      <AnimatePresence initial={false} mode="popLayout">
        <motion.span
          key={count}
          aria-hidden="true"
          initial={reduceMotion ? false : { y: 8, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={reduceMotion ? { opacity: 0, transition: { duration: 0 } } : { y: -8, opacity: 0 }}
          transition={saveCountTransition}
        >
          {countFormatter.format(count)}
        </motion.span>
      </AnimatePresence>
    </span>
  );
};

export const PdpEngagement = ({
  engagement,
  saveCountAdjustment = 0,
}: {
  engagement?: PropertyEngagement | null;
  saveCountAdjustment?: number;
}) => {
  if (!engagement) return null;
  const visibleViewCount =
    engagement.viewCount != null && engagement.viewCount >= MIN_VISIBLE_VIEW_COUNT
      ? engagement.viewCount
      : null;
  const saveCount =
    engagement.saveCount == null ? null : Math.max(0, engagement.saveCount + saveCountAdjustment);
  const visibleSaveCount =
    saveCount != null && saveCount >= MIN_VISIBLE_SAVE_COUNT ? saveCount : null;
  if (visibleViewCount == null && visibleSaveCount == null) return null;

  return (
    <Row
      aria-label="Home activity"
      className="flex-wrap items-center gap-3 text-od-content-secondary"
    >
      {visibleViewCount != null && (
        <Row className="items-center gap-1.5">
          <IconEye aria-hidden="true" size={18} stroke={2} className="text-od-content-primary" />
          <Text
            aria-label={`${formatActivityCount(visibleViewCount)} views`}
            className="whitespace-nowrap text-body-xs"
          >
            {formatActivityCount(visibleViewCount)}
            <span aria-hidden="true" className="hidden sm:inline">
              {' views'}
            </span>
          </Text>
        </Row>
      )}
      {visibleViewCount != null && visibleSaveCount != null && (
        <Box aria-hidden="true" className="h-3 w-px bg-od-border-primary" />
      )}
      {visibleSaveCount != null && (
        <Row className="items-center gap-1.5">
          <FavoriteHeartIcon filled size={14} />
          <Text
            aria-label={`${countFormatter.format(visibleSaveCount)} saves`}
            className="whitespace-nowrap text-body-xs leading-3 tracking-[-0.03em]"
          >
            <AnimatedSaveCount count={visibleSaveCount} />
            <span aria-hidden="true" className="hidden sm:inline">
              {' saves'}
            </span>
          </Text>
        </Row>
      )}
    </Row>
  );
};
